import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Add16Regular,
    Delete20Regular,
    LockClosed20Regular,
    MoreVertical16Regular,
    Share20Regular,
    Sparkle16Filled,
    TextEditStyle20Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import styles from './index.module.css';
import { getSectionVisibility } from './journalUtils';

const SectionsPane = ({
    sections,
    notesState,
    user,
    editingSectionName,
    editingSectionValue,
    onSectionSelect,
    onSectionNameChange,
    onSaveSectionName,
    onRenameSection,
    onShareSection,
    onMakePrivateClick,
    onSectionDeleteClick,
    onAddSection,
}) => {
    const navigate = useNavigate();

    return (
        <aside className={styles.sectionsPane}>
            <div className={styles.sectionsPaneHeader}>
                <h1 className={styles.journalTitle}>Journal</h1>
            </div>

            <div
                className={styles.insightsCard}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/journal/analysis')}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/journal/analysis');
                    }
                }}>
                <div className={styles.insightsLeft}>
                    <div className={styles.insightsIcon}>
                        <Sparkle16Filled color="#ffffff" />
                    </div>
                    <div className={styles.insightsMeta}>
                        <span className={styles.insightsTitle}>Journal Insights</span>
                        <span className={styles.insightsSubtitle}>Private workspace</span>
                    </div>
                </div>
                <span className={styles.insightsBadge}>Private</span>
            </div>

            <div className={styles.sectionsListHeader}>
                <span className={styles.sectionsLabel}>Sections</span>
                <button type="button" className={styles.newSectionBtn} onClick={onAddSection}>
                    <Add16Regular />
                    New section
                </button>
            </div>

            <div className={styles.sectionsList}>
                {sections.map((section, index) => {
                    const fullSection = notesState?.find(n => n.id === section.id) || section;
                    const entryCount = fullSection?.notes?.length || 0;
                    const visibility = getSectionVisibility(fullSection);
                    const entryLabel = `${entryCount} ${entryCount === 1 ? 'entry' : 'entries'}`;

                    return (
                        <div
                            key={section.id || index}
                            className={`${styles.sectionItem} ${section.isActive ? styles.activeSection : ''}`}
                            onClick={() => onSectionSelect(section)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (editingSectionName === section.id) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onSectionSelect(section);
                                }
                            }}>
                            <div className={styles.sectionMeta}>
                                {editingSectionName === section.id ? (
                                    <input
                                        type="text"
                                        value={editingSectionValue}
                                        onChange={onSectionNameChange}
                                        onKeyDown={e => {
                                            e.stopPropagation();
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                onSaveSectionName();
                                            }
                                        }}
                                        onBlur={onSaveSectionName}
                                        className={styles.sectionNameInput}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                    />
                                ) : (
                                    <div className={`${styles.sectionName} ${section.isActive ? styles.sectionNameActive : ''}`}>{section.name}</div>
                                )}
                                <div className={styles.sectionSubInfo}>
                                    <span>{entryLabel}</span>
                                    <span>·</span>
                                    <span>{visibility}</span>
                                </div>
                            </div>
                            {user?.id === section?.created_by && !section.is_project_default && !section.is_ask_lens_output && (
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            aria-label="Section actions"
                                            onClick={e => e.stopPropagation()}>
                                            <MoreVertical16Regular color="var(--Neutral-Gray-6)" />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem onClick={() => onRenameSection(section.id, section.name)}>
                                                <TextEditStyle20Regular className="me-1" /> Rename Section
                                            </MenuItem>
                                            <MenuItem onClick={() => onShareSection(section)}>
                                                <Share20Regular className="me-1" /> Share Section
                                            </MenuItem>
                                            <MenuItem
                                                disabled={!section.shared_with || section.shared_with.length === 0 || section.is_project_default}
                                                onClick={() => onMakePrivateClick(section)}>
                                                <LockClosed20Regular className="me-1" /> Make Private
                                            </MenuItem>
                                            <MenuItem onClick={() => onSectionDeleteClick(section)}>
                                                <Delete20Regular className="me-1" /> Delete Section
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            )}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
};

export default SectionsPane;
